import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { TableCell, TableRow, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";

import Table from "../../components/Table";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import EmptyState from "../../components/EmptyState";
import Pagination from "../../components/Pagination";
import SearchInput from "../../components/SearchInput";
import { ROUTE_PATHS } from "../../utils/RoutesPaths";
import { DeleteBgIcon, EditBgIcon } from "../../utils/Icons";
import {
  renderTextEllipsis,
  setPageMainTitle,
  textContainOnlySpaces,
} from "../../utils/Helpers";
import { isSuperAdmin } from "../../utils/Permissions";
import messages from "../../assets/locale/messages";
import {
  getAdminsRequest,
  deleteAdminRequest,
} from "../../store/Admins/actions";
import "./Admins.scss";

const Admins = () => {
  const lang = useSelector((state) => state.locale.lang);
  const isLoading = useSelector((state) => state.loader);
  const meta = useSelector((state) => state.admins.meta);
  const list = useSelector((state) => state.admins.list);

  const history = useHistory();
  const dispatch = useDispatch();

  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(1);
  const [deleteAdminId, setDeleteAdminId] = useState(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);

  const { shared, admins, pagesTitles } = messages[lang];

  const getAdmins = (pageNo, search) => {
    dispatch(
      getAdminsRequest({
        page: pageNo,
        items: 10,
        query: search,
      })
    );
  };

  useEffect(() => {
    setPageMainTitle("adminsManagement");
  }, []);

  useEffect(() => {
    getAdmins(page, searchValue);
  }, [page]);

  useEffect(() => {
    if (
      (searchValue.length >= 3 || searchValue.length === 0) &&
      !textContainOnlySpaces(searchValue)
    ) {
      setPage(1);
      getAdmins(1, searchValue);
    }
  }, [searchValue]);

  const handleEditAdmin = (id) => {
    history.push(ROUTE_PATHS.editAdmin.replace(":id", id));
  };

  const handleDeleteClick = (id) => {
    setDeleteAdminId(id);
    setOpenDeleteConfirm(true);
  };

  const handleDeleteAdmin = () => {
    dispatch(
      deleteAdminRequest({
        id: deleteAdminId,
        currentPage: meta.currentPage,
      })
    );
    setOpenDeleteConfirm(false);
  };

  const handleCloseDeleteConfirmModal = () => {
    setOpenDeleteConfirm(false);
  };

  const renderHeaderSection = (showActions) => (
    <div className="admins-header d-flex justify-content-between mb-4">
      <div className="title-search d-flex flex-column flex-md-row">
        <Typography
          variant="h4"
          sx={{ flexGrow: 1 }}
          className={`page-title ${lang === "en" ? "me-5" : "ms-5"}`}
        >
          {pagesTitles.admins}
        </Typography>
        {showActions && (
          <SearchInput
            name="search"
            value={searchValue}
            placeholder={admins.list.search}
            onChange={(value) => {
              setSearchValue(value);
            }}
          />
        )}
      </div>
      {isSuperAdmin() && showActions && (
        <div className="add-btn-wrapper">
          <Button
            label={
              <span className="d-flex align-items-center">
                <Add fontSize="large" />
                <span className="ps-1 ">{admins.list.CreateAdminButton}</span>
              </span>
            }
            labelClass="fsize-16 text-white pe-2"
            onClick={() => {
              history.push(ROUTE_PATHS.addAdmin);
            }}
          />
        </div>
      )}
    </div>
  );

  const adminsList = () => {
    if (!isLoading && list?.length === 0 && !searchValue) {
      return (
        <>
          {renderHeaderSection(false)}
          <div className="bg-white rounded-4 admins-container d-flex justify-content-center align-items-center flex-column">
            <EmptyState
              subTitle={shared.noData}
              description={admins.list.noDataDescription}
              ButtonLabel={admins.list.CreateAdminButton}
              handleBtnAction={() => {
                history.push(ROUTE_PATHS.addAdmin);
              }}
              showActionButton={isSuperAdmin()}
            />
          </div>
        </>
      );
    } else if (!isLoading && searchValue && list?.length === 0) {
      return (
        <>
          {renderHeaderSection(true)}
          <div className="bg-white rounded-4 admins-container d-flex justify-content-center align-items-center flex-column">
            <EmptyState subTitle={shared.noSearch} showActionButton={false} />
          </div>
        </>
      );
    } else {
      return (
        <>
          <div>
            {renderHeaderSection(true)}
            <div className="bg-white rounded-4 admins-table-list">
              <Table
                className="rounded-4"
                headlines={[admins.list.username, admins.list.mail, admins.list.role]}
                hasActions={isSuperAdmin()}
                rows={list?.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell className="start font-regular text-dark-blue fsize-14 ">
                      {renderTextEllipsis(row.username || '-')}
                    </TableCell>
                    <TableCell className="font-regular text-dark-blue fsize-14 ">{row.email}</TableCell>
                    <TableCell className="font-regular text-dark-blue fsize-14 ">
                      {row.is_super_admin ? admins.list.superAdmin : admins.list.admin}
                    </TableCell>
                    {isSuperAdmin() && (
                      <TableCell className="font-regular text-dark-blue fsize-14">
                        <div className="table-icons-container gap-4">
                          <EditBgIcon className="pointer" onClick={() => handleEditAdmin(row.id)} />
                          {!row.is_super_admin && (
                            <DeleteBgIcon className="pointer" onClick={() => handleDeleteClick(row.id)} />
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              />
              <div className="d-flex justify-content-center py-3">
                {meta?.pages > 1 && (
                  <Pagination
                    count={meta?.pages}
                    page={meta?.currentPage || page}
                    handleChange={(event, page) => {
                      setPage(page);
                    }}
                    defaultPage={1}
                  />
                )}
              </div>
            </div>
          </div>
          <Modal
            open={openDeleteConfirm}
            handleClose={handleCloseDeleteConfirmModal}
            title={admins.deleteAdmin.title}
            contentClass="mb-5 pb-2 text-primary"
            content={<span>{admins.deleteAdmin.deleteConfirmationMsg}</span>}
            confirmBtnTxt={shared.confirm}
            isContentText={false}
            maxWidth="xs"
            cancelBtnTxt={shared.cancel}
            handleConfirm={handleDeleteAdmin}
            handleCancel={handleCloseDeleteConfirmModal}
            modalClass="delete-admin-modal"
            actionsClass="container px-4"
            confirmBtnClass="btn-secondary fsize-14 text-white py-2 "
            cancelBtnClass="fsize-14 py-2"
          />
        </>
      );
    }
  };

  return <div className="admins-page">{adminsList()}</div>;
};

export default Admins;
